import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { EmailActions } from 'store/reducers/email';
import { Container, Box, Typography, Button, CircularProgress, ListItem, Stack } from '@mui/material';
import { FormTextField } from 'shared/components/Form';
import { Logo, TelegramIcon, XIcon, LinkedInIcon, InstagramIcon } from 'icons';
import parse from 'html-react-parser';
import { useStyles } from './styles';

interface Message {
	email: string;
}

export default function TemporarilyClosed() {
	const { classes } = useStyles();

	const dispatch = useDispatch();

	// Selectors
	const {
		email: {
			subscribeEmail: { sent: emailSent, loading },
		},
		account: { account },
	} = useSelector((state) => state);

	const onSubmit = (values: any) => {
		dispatch(
			EmailActions.subscribeEmail.sendRequest({
				...values,
				interest: 'nft-redesign-waitlist',
			}),
		);
	};

	const getInitialValues = ({ email = account?.email || '' }: Message) => ({ email });

	const socials = [
		{ link: 'https://twitter.com/blocksportdotio', icon: <XIcon /> },
		{ link: 'https://www.linkedin.com/company/blocksport', icon: <LinkedInIcon /> },
		{ link: 'https://www.instagram.com/blocksportdotio/', icon: <InstagramIcon /> },
		{ link: 'https://t.me/Blocksport_official ', icon: <TelegramIcon /> },
	];

	return (
		<Container fixed className={classes.root}>
			<Box className={classes.wrapper}>
				<Box className={classes.logoBox}>
					<Logo />
				</Box>

				<Box className={classes.content}>
					<Box className={classes.lcol}>
						<Typography variant="h1" className={classes.h1}>
							{parse(`We’re Upgrading for \na Better Experience`)}
						</Typography>
						<Typography variant="body1" className={classes.description}>
							{parse(
								`Our marketplace is temporarily closed for improvements. \nWe’re working behind the scenes to bring you a faster, \nsmarter and more exciting experience.`,
							)}
						</Typography>

						<Box className={classes.formWrapper}>
							<Typography variant="subtitle1" className={classes.label}>
								{emailSent ? "Thank you for subscribing! We'll keep you updated on our exciting site redesign and relaunch. 🎉" : 'Get notified when we launch'}
							</Typography>

							{loading ? (
								<Box display="flex" justifyContent="flex-start" className={classes.formLoader}>
									<CircularProgress />
								</Box>
							) : (
								!emailSent && (
									<Formik
										initialValues={getInitialValues({} as Message)}
										validationSchema={Yup.object().shape({
											email: Yup.string()
												.nullable()
												.email('Email should be valid')
												.required('Email is required field')
												.max(253, "Email can't be longer than 253 symbols"),
										})}
										onSubmit={onSubmit}
										validateOnChange={true}
										validateOnMount={false}
										enableReinitialize
									>
										{({ dirty, isValid, values, setFieldValue }) => {
											return (
												<Form>
													<Box className={classes.form}>
														<FormTextField istouched={0} name="email" type="email" placeholder="Enter your email address" fullWidth className={classes.formTextField} />
														<Button type="submit" variant="contained" disabled={loading || !isValid} fullWidth className={classes.appFormSubmit}>
															Notify me
														</Button>
													</Box>
												</Form>
											);
										}}
									</Formik>
								)
							)}
						</Box>
					</Box>

					<Box className={classes.rcol}>
						<img src="https://prod-blocksport-nft-data.s3.eu-central-1.amazonaws.com/Web3MadeEasy.svg" alt="Web3 Made Easy" />
					</Box>
				</Box>

				<Box className={classes.footer}>
					<Stack direction="row" className={classes.linksContainer}>
						{socials.map((item) => (
							<ListItem key={item.link}>
								<a target="_blank" href={item.link} rel="noreferrer">
									{item.icon}
								</a>
							</ListItem>
						))}
					</Stack>
					<Typography variant="caption" className={classes.copyright}>
						{`© ${new Date().getFullYear()} Blocksport AG, Dammstrasse 16, 6300 Zug, Switzerland`}
					</Typography>
				</Box>
			</Box>
		</Container>
	);
}
